import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { SAVED_VIEW_QUERY_OPTIONS } from '@client/components/table-views/saved-view-query-options';
import { useAppStateStore } from '@client/stores/AppStateStore';
import { useAuthStore } from '@client/stores/AuthStore';
import { useDebugStore } from '@client/stores/DebugStore';
import { expireSession, onSessionExpired } from '@client/utils/auth/sessionExpiry';
import { calculateRetryDelay, isNetworkError } from '@client/utils/errors/network-error-detection';
import { getFlagSync } from '@client/utils/feature-flags';
import { debug } from '@client/utils/logger';
import { getPublicFormCaptchaTokensForOperation } from '@client/utils/turnstile/public-form-captcha-tokens';
import * as Sentry from '@sentry/react';
import type { AppRouter } from '@server/routes/root';
import { HTTP_HEADER, LOCAL_STORAGE_KEY } from '@shared/constants';
import { type Mutation, MutationCache, type Query, QueryCache, QueryClient } from '@tanstack/react-query';
import type { TRPCClientErrorLike, TRPCLink } from '@trpc/client';
import {
    createTRPCQueryUtils,
    createTRPCReact,
    getQueryKey,
    type inferReactQueryProcedureOptions,
} from '@trpc/react-query';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { observable } from '@trpc/server/observable';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import Decimal from 'decimal.js';
import { DateTime } from 'luxon';
import { SuperJSON } from 'superjson';
import { buildCaptchaHeaders } from './captcha-headers';
import { isEdgeChallengeError } from './edge-response';
import { isCaptureLeftToServer, isExpectedSessionExpiration, shouldCaptureToSentry } from './error-classification';
import { getAuthSessionSnapshot, handleGlobalUnauthorizedError } from './global-error-handling';
import { createHttpTransportLink } from './http-transport-link';
import { readOrgIdFromUrl, resolveRequestOrgId } from './orgIdHeader';
import { DEFAULT_QUERY_ERROR_SURFACE, type ErrorSurface } from './query-meta';
import {
    buildSessionExpiredError,
    isProtectedRoute,
    isPublicRoute,
    isUnauthorizedTrpcError,
    shouldAttachAuthHeader,
    shouldBlockExpiredRequest,
    shouldBlockLoggingOutRequest,
    shouldBlockLoginRedirectRequest,
    shouldClearExpiredTokenOnPublicRoute,
    shouldEnableTrpcQuery,
    shouldRetryTrpcMutation,
    shouldRetryTrpcQuery,
} from './sessionHandling';

export type { AppRouter };

const ERROR_SURFACES = new Set<ErrorSurface>(['silent', 'inline', 'modal', 'route', 'boundary']);

export type ReactQueryOptions = inferReactQueryProcedureOptions<AppRouter>;

export type RouterInputs = inferRouterInputs<AppRouter>;

export type RouterOutputs = inferRouterOutputs<AppRouter>;

export type RouterError = TRPCClientErrorLike<AppRouter>;

export const trpc = createTRPCReact<AppRouter>();

export type TrpcClient = ReturnType<typeof trpc.useUtils>;

export type StaleTime = number | 'immediate' | 'infinite' | 'default';

export function getStaleTime(staleTime?: StaleTime) {
    if (!staleTime || typeof staleTime === 'number') {
        return staleTime;
    }

    return {
        immediate: 0,
        infinite: Infinity,
        default: undefined,
    }[staleTime];
}

function isRecord(value: unknown): value is Record<PropertyKey, unknown> {
    return value !== null && typeof value === 'object';
}

function isErrorSurface(value: unknown): value is ErrorSurface {
    return typeof value === 'string' && ERROR_SURFACES.has(value as ErrorSurface);
}

/**
 * Capture a query/mutation failure to Sentry on behalf of the layered error
 * handling model — this is the single Sentry owner for the cache layer.
 *
 * Skipped when:
 * - the meta opts out via `captureToSentry: false`;
 * - the {@link shouldCaptureToSentry} classifier declines (downstream owner,
 *   expected session expiration, network error).
 *
 * @param source The cache that owns the failure (`'queryCache'` / `'mutationCache'`).
 *               Used as a Sentry tag so dashboards can distinguish the two.
 * @param surface The declared {@link ErrorSurface} for this query/mutation, or
 *                `undefined` to fall back to {@link DEFAULT_QUERY_ERROR_SURFACE}.
 * @param explicitOptOut `true` when the meta sets `captureToSentry: false`.
 */
function captureCacheError(
    error: unknown,
    source: 'queryCache' | 'mutationCache',
    surface: ReturnType<typeof getMetaSurface>,
    explicitOptOut: boolean,
    key: readonly unknown[] | undefined,
) {
    if (explicitOptOut) return;

    if (isEdgeChallengeError(error)) {
        reportEdgeChallenge(source, readTrpcPath(key));
        return;
    }

    const session = getAuthSessionSnapshot();
    const effectiveSurface = surface ?? DEFAULT_QUERY_ERROR_SURFACE;
    if (!shouldCaptureToSentry(error, effectiveSurface, session)) return;

    const errorWithData = error as { data?: { code?: string }; message?: string };
    // The key's procedure path tags every capture, and lets `beforeSend` fingerprint a
    // server-answered error (one with `data.code`) that carries no `data.path` by procedure;
    // see `applyTrpcClientErrorGrouping`.
    const trpcPath = readTrpcPath(key);
    Sentry.captureException(error, {
        level: 'error',
        tags: {
            location: source,
            errorSurface: effectiveSurface,
            ...(trpcPath === undefined ? {} : { trpcPath }),
        },
        extra: {
            orgId: readOrgIdFromUrl(window.location.pathname),
            isPublicSection: isPublicRoute(window.location.pathname),
            url: window.location.href,
            userAgent: navigator.userAgent,
            errorCode: errorWithData?.data?.code,
            errorMessage: errorWithData?.message,
        },
    });
}

/** Share of page loads whose first edge challenge is reported; automated clients produce most of them. */
export const EDGE_CHALLENGE_SAMPLE_RATE = 0.1;
let edgeChallengeReported = false;

/**
 * A Cloudflare browser challenge is not an application error, so it never raises an
 * error-level issue (CURIUM-FE-RE). It still leaves a sampled warning, grouped into a single
 * issue and tagged with the procedure, so a Cloudflare rule change that starts challenging
 * real visitors on `/trpc` stays measurable. Reported at most once per page load, because a
 * challenged page fails every request until it reloads.
 */
function reportEdgeChallenge(source: 'queryCache' | 'mutationCache', path: string | undefined) {
    if (edgeChallengeReported) return;
    edgeChallengeReported = true;
    if (Math.random() >= EDGE_CHALLENGE_SAMPLE_RATE) return;

    Sentry.captureMessage('tRPC request challenged at edge', {
        level: 'warning',
        fingerprint: ['trpc-edge-challenge'],
        tags: {
            location: source,
            trpcPath: path ?? 'unknown',
            isPublicSection: isPublicRoute(window.location.pathname),
        },
        extra: { sampleRate: EDGE_CHALLENGE_SAMPLE_RATE },
    });
}

/** Test hook: forget that this page load already reported an edge challenge. */
export function resetEdgeChallengeReportForTests() {
    edgeChallengeReported = false;
}

/** The dotted procedure path from a tRPC query or mutation key, such as `org.getOrgIdByKey`. */
function readTrpcPath(key: readonly unknown[] | undefined): string | undefined {
    const segments = key?.[0];
    if (!Array.isArray(segments) || !segments.every((segment) => typeof segment === 'string')) return undefined;
    return segments.length > 0 ? segments.join('.') : undefined;
}

/**
 * Read the typed `errorSurface` declared on a query/mutation's meta (see
 * `query-meta.ts`). Returns `undefined` when the call site hasn't opted in,
 * which the cache handlers interpret as {@link DEFAULT_QUERY_ERROR_SURFACE}.
 */
function getMetaSurface(
    container: Query<unknown, unknown, unknown> | Mutation<unknown, unknown, unknown, unknown>,
): ErrorSurface | undefined {
    const meta = container.meta;
    if (!isRecord(meta)) return undefined;
    return isErrorSurface(meta.errorSurface) ? meta.errorSurface : undefined;
}

/**
 * Read the typed `captureToSentry: false` opt-out from a query/mutation's
 * meta. Only an explicit `false` counts — `undefined` means "use the default
 * Sentry policy for this surface".
 */
function getMetaCaptureOptOut(
    container: Query<unknown, unknown, unknown> | Mutation<unknown, unknown, unknown, unknown>,
): boolean {
    const meta = container.meta;
    if (!isRecord(meta)) return false;
    return meta.captureToSentry === false;
}

/**
 * Handle the unusual case of an UNAUTHORIZED tRPC error arriving from a query
 * fired by a public (unauthenticated) section of the app. Explicit inline/silent
 * owners handle expected failures such as CAPTCHA rejection themselves. For
 * other surfaces, show a generic modal and capture the unexpected auth failure,
 * unless the server marked it `clientCapture: false` (a refused captcha challenge
 * it has already recorded, CUR-1962).
 *
 * @returns `true` when the error matched this case and the caller should bail
 *          out (skipping the normal Sentry-capture/modal-dispatch path).
 */
function maybeShowUnauthorizedPublicSectionError(error: unknown, surface: ErrorSurface | undefined) {
    if (!isUnauthorizedTrpcError(error)) return false;
    if (!isPublicRoute(window.location.pathname)) return false;
    if (surface === 'inline' || surface === 'silent') return true;

    if (!isCaptureLeftToServer(error)) {
        Sentry.captureException(new Error('Unauthorized error in public section'), {
            level: 'error',
            tags: { location: 'queryCache', errorType: 'unexpected_auth_error' },
            extra: { url: window.location.href, error },
        });
    }

    if (errorMessage.isInitialised) {
        errorMessage.show('An unexpected error occurred. Please try again.');
    }
    return true;
}

export const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: (error, query) => {
            handleGlobalUnauthorizedError(error);

            const surface = getMetaSurface(query);
            const optOut = getMetaCaptureOptOut(query);

            // Public inline/silent queries own expected UNAUTHORIZED failures, including CAPTCHA rejection.
            const handledAsPublicUnauth = maybeShowUnauthorizedPublicSectionError(error, surface);
            if (handledAsPublicUnauth) return;

            captureCacheError(error, 'queryCache', surface, optOut, query.queryKey);

            // Modal dispatch — opt-in via `meta.errorSurface: 'modal'`, with lifecycle guards
            // so preloads, background invalidations and pre-AppProvider failures do not pop a modal.
            if (surface !== 'modal') return;
            if (!errorMessage.isInitialised) return;
            if (isExpectedSessionExpiration(error, getAuthSessionSnapshot())) return;
            if (query.getObserversCount() === 0) return;
            errorMessage.show(error);
        },
    }),
    mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
            handleGlobalUnauthorizedError(error);

            const surface = getMetaSurface(mutation);
            const optOut = getMetaCaptureOptOut(mutation);

            // Dev-only mutex check: `errorSurface: 'modal'` should not be combined with a local
            // `onError` callback — pick one owner. See plan §1b.
            if (import.meta.env.DEV && surface === 'modal' && typeof mutation.options.onError === 'function') {
                console.error(
                    "Curium: mutation has both `errorSurface: 'modal'` and a local `onError` — choose one. (Mutation path: %s)",
                    mutation.options.mutationKey?.map(String).join('.') ?? '<anonymous>',
                );
            }

            captureCacheError(error, 'mutationCache', surface, optOut, mutation.options.mutationKey);

            if (surface !== 'modal') return;
            if (!errorMessage.isInitialised) return;
            if (isExpectedSessionExpiration(error, getAuthSessionSnapshot())) return;
            errorMessage.show(error);
        },
    }),
    defaultOptions: {
        queries: {
            retry: (failureCount, error) => {
                return shouldRetryTrpcQuery({
                    authTokenExpired: useAuthStore.getState().authTokenExpired,
                    isProtectedRoute: isProtectedRoute(window.location.pathname),
                    failureCount,
                    error,
                });
            },
            retryDelay: (attemptIndex, error) => {
                // Use exponential backoff for network errors
                if (isNetworkError(error)) {
                    return calculateRetryDelay(attemptIndex);
                }
                return 1000; // Default 1 second
            },
            enabled: () =>
                shouldEnableTrpcQuery({
                    authTokenExpired: useAuthStore.getState().authTokenExpired,
                    isProtectedRoute: isProtectedRoute(window.location.pathname),
                }),
        },
        mutations: {
            retry: (failureCount, error) => {
                return shouldRetryTrpcMutation({
                    authTokenExpired: useAuthStore.getState().authTokenExpired,
                    failureCount,
                    error,
                });
            },
            retryDelay: (attemptIndex, error) => {
                if (isNetworkError(error)) {
                    return calculateRetryDelay(attemptIndex);
                }
                return 1000;
            },
        },
    },
});

// The login warmup seeds per-entity `savedView.listSavedViews` caches via `setData` (no observer),
// which would otherwise inherit React Query's default 5-min gcTime. Pin the documented 60-min
// survival window (see SAVED_VIEW_QUERY_OPTIONS) so the warm cache survives the login→navigate idle
// gap with stale-while-revalidate instead of cold-loading after 5 min.
queryClient.setQueryDefaults(getQueryKey(trpc.savedView.listSavedViews), {
    gcTime: SAVED_VIEW_QUERY_OPTIONS.gcTime,
});

onSessionExpired(() => queryClient.cancelQueries());

SuperJSON.registerCustom<Decimal, string>(
    {
        isApplicable(value): value is Decimal {
            return Decimal.isDecimal(value);
        },
        serialize(value) {
            return value.toJSON();
        },
        deserialize(value) {
            return new Decimal(value);
        },
    },
    'decimal.js',
);

// Track if we've already logged session expiration to avoid console spam
let hasLoggedSessionExpiration = false;

// Connection monitoring link - tracks network errors and updates app state
const connectionMonitorLink: TRPCLink<AppRouter> = () => {
    return ({ next, op }) => {
        return observable((observer) => {
            const unsubscribe = next(op).subscribe({
                next(value) {
                    // Successful response - reset connection failures
                    const appState = useAppStateStore.getState();
                    if (appState.connectionState.consecutiveFailures > 0) {
                        appState.actions.resetConnectionFailures();
                    }
                    observer.next(value);
                },
                complete() {
                    observer.complete();
                },
                error(error) {
                    // Track connection failures for network errors
                    if (isNetworkError(error)) {
                        const appState = useAppStateStore.getState();
                        appState.actions.recordConnectionFailure();
                    }
                    observer.error(error);
                },
            });

            return unsubscribe;
        });
    };
};

const authTokenCheckLink: TRPCLink<AppRouter> = () => {
    return ({ next, op }) => {
        return observable((observer) => {
            // Anonymous public routes and Auth0 callback routes must not be blocked by the
            // expired-session guard: they make legitimate unauthenticated tRPC calls, and
            // would otherwise break for any visitor whose localStorage still carries a
            // stale authTokenExpired flag from a prior signed-in session.
            const onProtectedRoute = isProtectedRoute(window.location.pathname);
            const authStore = useAuthStore.getState();
            const hasToken = authStore.isTokenSet();
            const isExpired = authStore.isTokenExpired();

            // RequireAuth is sending the tab to /login after a session failure. The URL already
            // reads /login while the protected page is still mounted, so neither the public-route
            // branch below nor the protected-route guards after it apply; refuse the request
            // locally so it cannot leave without a token (CUR-2473).
            if (
                shouldBlockLoginRedirectRequest({
                    isRedirectingToLogin: authStore.isRedirectingToLogin,
                    pathname: window.location.pathname,
                })
            ) {
                debug('[tRPC] Login redirect in progress, refusing request in authTokenCheckLink', {
                    operation: op.path,
                    type: op.type,
                    currentPath: window.location.pathname,
                    timestamp: new Date().toISOString(),
                });
                observer.error(buildSessionExpiredError());
                return;
            }

            if (!onProtectedRoute) {
                if (
                    shouldClearExpiredTokenOnPublicRoute({
                        hasToken,
                        isExpired,
                        isProtectedRoute: onProtectedRoute,
                    })
                ) {
                    debug('[tRPC] Clearing expired token on public route before anonymous request', {
                        operation: op.path,
                        type: op.type,
                        currentPath: window.location.pathname,
                        timestamp: new Date().toISOString(),
                    });
                    authStore.clearAuthToken();
                    hasLoggedSessionExpiration = false;
                }

                const unsubscribe = next(op).subscribe({
                    next(value) {
                        observer.next(value);
                    },
                    complete() {
                        observer.complete();
                    },
                    error(error) {
                        observer.error(error);
                    },
                });
                return unsubscribe;
            }

            // A deliberate logout has cleared the token and is waiting on the Auth0 redirect.
            // Refuse the request locally: it must not reach the API unauthenticated, and this
            // is not an expiry, so expireSession() is not called (CUR-2010).
            if (
                shouldBlockLoggingOutRequest({
                    isLoggingOut: authStore.isLoggingOut,
                    isProtectedRoute: onProtectedRoute,
                })
            ) {
                debug('[tRPC] Logout in progress, refusing request in authTokenCheckLink', {
                    operation: op.path,
                    type: op.type,
                    currentPath: window.location.pathname,
                    timestamp: new Date().toISOString(),
                });
                observer.error(buildSessionExpiredError());
                return;
            }

            // Block requests if:
            // - A token exists and is expired (first detection), OR
            // - The session has already been flagged as expired (token already cleared)
            // This ensures later protected operations during the redirect window cannot
            // reach the API unauthenticated after clearAuthToken() wipes the token.
            // - No token and not flagged expired (before login) → Allow request, server handles 401 (needed for /callback)
            if (
                shouldBlockExpiredRequest({
                    hasToken,
                    isExpired,
                    authTokenExpired: authStore.authTokenExpired,
                    isProtectedRoute: onProtectedRoute,
                })
            ) {
                debug('[tRPC] Token expired detected in authTokenCheckLink', {
                    operation: op.path,
                    type: op.type,
                    currentPath: window.location.pathname,
                    timestamp: new Date().toISOString(),
                });

                expireSession(
                    '[tRPC] Token expired detected in authTokenCheckLink, clearing auth state and cancelling active queries',
                );

                if (!hasLoggedSessionExpiration) {
                    debug(
                        '[Auth] Session expired. User will be prompted to log in again. Subsequent tRPC calls will be blocked.',
                    );
                    hasLoggedSessionExpiration = true;
                }

                observer.error(buildSessionExpiredError());
                return;
            } else {
                // Reset the flag when session is active again (after re-login)
                hasLoggedSessionExpiration = false;
            }

            // Continue with the request
            // If no token on auth route, let it through - server will handle 401
            const unsubscribe = next(op).subscribe({
                next(value) {
                    observer.next(value);
                },
                complete() {
                    observer.complete();
                },
                error(error) {
                    observer.error(error);
                },
            });

            return unsubscribe;
        });
    };
};

// HTTP link for queries and mutations
const httpTransportLink = createHttpTransportLink<AppRouter>({
    url: location.origin + '/trpc',
    async headers(opList) {
        const headers: Record<string, string> = {
            [HTTP_HEADER.PAGE_URL]: window.location.href,
            [HTTP_HEADER.USER_TIMEZONE]: DateTime.now().zoneName,
        };

        let fingerprint: string | null = null;
        try {
            fingerprint = localStorage.getItem(LOCAL_STORAGE_KEY.FINGERPRINT);
        } catch {
            // Storage unavailable — fingerprint header will be omitted
        }
        if (fingerprint) {
            headers[HTTP_HEADER.FINGERPRINT] = fingerprint;
        }

        Object.assign(headers, await buildCaptchaHeaders(opList, getPublicFormCaptchaTokensForOperation));
        const token = useAuthStore.getState().getAuthToken();
        if (
            shouldAttachAuthHeader({
                hasToken: Boolean(token),
                pathname: window.location.pathname,
            }) &&
            token
        ) {
            headers[HTTP_HEADER.AUTHORIZATION] = `Bearer ${token}`;

            // X-Curium-Org-Id is the canonical signal for which org the SPA wants to operate in.
            const authState = useAuthStore.getState();
            const orgIdOverride = opList[0].context.orgIdOverride;
            const orgId = resolveRequestOrgId({
                orgIdOverride,
                pendingOrgId: authState.pendingOrgId,
                storeOrgId: authState.org?.id,
                urlOrgId: readOrgIdFromUrl(window.location.pathname),
            });
            if (orgId !== undefined) {
                headers[HTTP_HEADER.ORG_ID] = orgId.toString();
            }
        }

        const debugStore = useDebugStore.getState();
        const postHogDebugFlag = getFlagSync('enable-debug-info', false);
        const isDebugInfoEnabled = postHogDebugFlag || debugStore.isForceDebugEnabledFlag;
        const isUIDebugInfoEnabled = debugStore.isUIDebugInfoEnabledFlag && postHogDebugFlag;

        if (isDebugInfoEnabled && isUIDebugInfoEnabled) {
            headers[HTTP_HEADER.DEBUG_INFO] = 'true';
        }

        return headers;
    },
});

export const trpcClient = trpc.createClient({
    links: [connectionMonitorLink, authTokenCheckLink, httpTransportLink],
});

export const trpcQueryUtils = createTRPCQueryUtils({
    queryClient,
    client: trpcClient,
});

/**
 * The `@trpc/tanstack-react-query` integration (CUR-1605): typed `queryOptions`, `mutationOptions`
 * and `subscriptionOptions`, plus keys and filters, for TanStack Query's own hooks and `queryClient`.
 *
 * It shares `trpcClient` and `queryClient` with the classic `trpc` hooks and builds the same query
 * keys, so a query read through one integration is invalidated through the other while the
 * codebase migrates. See `docs/guidelines/trpc-react-query.md`.
 */
export const trpcOptions = createTRPCOptionsProxy<AppRouter>({
    client: trpcClient,
    queryClient,
});
